import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { api, ApiError } from '../api.ts';
import { Avatar, AVATAR_EMOJI, type AvatarData } from './Avatar.tsx';

interface Mine {
  avatar: AvatarData;
  options: { colors: string[]; icons: string[] };
}

// La photo est recadrée en carré et réduite dans le navigateur avant l'envoi : léger à transférer, et rien d'autre que l'image n'est conservé.
const SIZE = 512;

async function squareJpeg(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('INVALID_IMAGE'));
      el.src = url;
    });
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = Math.min(SIZE, side);
    canvas.getContext('2d')!.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('INVALID_IMAGE'))), 'image/jpeg', 0.86));
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function AvatarEditor({ name }: { name: string }) {
  const [mine, setMine] = useState<Mine | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const load = () => api.get<Mine>('/avatars/me').then(setMine).catch(() => undefined);
  useEffect(() => {
    void load();
  }, []);

  async function run(action: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      await load();
      setMessage({ kind: 'ok', text: ok });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : (err as Error).message;
      setMessage({ kind: 'error', text: code === 'FILE_TOO_LARGE' ? 'Image trop lourde.' : code === 'INVALID_IMAGE' || code === 'INVALID_FILE' ? 'Ce fichier n’est pas une image utilisable (JPEG, PNG ou WebP).' : 'Enregistrement impossible, réessayez.' });
    } finally {
      setBusy(false);
    }
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await run(async () => {
      const blob = await squareJpeg(file);
      const form = new FormData();
      form.append('file', blob, 'photo.jpg');
      const res = await fetch('/api/avatars/me/photo', { method: 'POST', credentials: 'include', body: form });
      if (!res.ok) throw new ApiError((await res.json().catch(() => ({}))).error ?? 'REQUEST_FAILED', res.status);
    }, 'Photo enregistrée.');
  }

  if (!mine) return null;
  const { avatar, options } = mine;
  const style = (patch: { color?: string | null; icon?: string | null }) =>
    run(() => api.put('/avatars/me/style', { color: patch.color === undefined ? avatar.color : patch.color, icon: patch.icon === undefined ? avatar.icon : patch.icon }), 'Avatar mis à jour.');

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <Avatar avatar={avatar} size={72} label={name} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800 }}>Ma photo</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            Les établissements voient votre photo seulement après votre candidature. Sans photo, votre avatar s’affiche.
          </div>
        </div>
      </div>

      {message && (message.kind === 'ok' ? <div className="badge badge-available" style={{ padding: '10px 14px' }}>{message.text}</div> : <div className="error-banner">{message.text}</div>)}

      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} style={{ display: 'none' }} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => input.current?.click()}>
          {avatar.photoUrl ? 'Changer la photo' : 'Ajouter une photo'}
        </button>
        {avatar.photoUrl && (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => run(() => api.delete('/avatars/me/photo'), 'Photo retirée.')}>
            Retirer la photo
          </button>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Avatar personnalisable</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }} role="radiogroup" aria-label="Couleur de l’avatar">
          {options.colors.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={avatar.color === c} aria-label={`Couleur ${c}`} disabled={busy} onClick={() => style({ color: c })}
              style={{ width: 30, height: 30, borderRadius: '50%', background: c, border: avatar.color === c ? '3px solid var(--text-primary)' : '2px solid transparent', cursor: 'pointer' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="radiogroup" aria-label="Pictogramme de l’avatar">
          <button type="button" role="radio" aria-checked={avatar.icon === null} className={`btn btn-sm ${avatar.icon === null ? 'btn-primary' : 'btn-secondary'}`} disabled={busy} onClick={() => style({ icon: null })}>
            Initiales
          </button>
          {options.icons.map((i) => (
            <button key={i} type="button" role="radio" aria-checked={avatar.icon === i} aria-label={i} className={`btn btn-sm ${avatar.icon === i ? 'btn-primary' : 'btn-secondary'}`} style={{ minWidth: 40 }} disabled={busy} onClick={() => style({ icon: i })}>
              {AVATAR_EMOJI[i]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
